import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import {
    getCurrentUser,
    getSession,
    loginFormScenario,
} from "components/helper/callApi";
import { useUser } from "components/helper/userContext";
import { useToken } from "components/helper/tokenContext";
import { useSession } from "components/helper/sessionContext";
import EmailForm from "components/authentificationMode/common/EmailForm";
import EmailPasswordForm from "components/authentificationMode/common/EmailPasswordForm";

import { HTTPResponseError } from "services/errors";
import { getAdditionalFields } from "services/additionalFields";
import { User } from "types/User";

import { SessionType } from "types/sessionType";
import { Scenario } from "types/scenarioType";

type Props = {
    codeOnline: string;
    scenario: Scenario;
    handleSubmit?: (email: string, password: string) => Promise<void>;
};

const LoginPassword = ({
    codeOnline,
    scenario,
    handleSubmit: handleSubmitBase,
}: Props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { setToken, removeToken } = useToken();

    const userContext = useUser();
    const { setSession } = useSession();

    const handleSubmit =
        handleSubmitBase !== undefined
            ? handleSubmitBase
            : async (email: string, password: string) => {
                  let userData: User | null = null;
                  try {
                      const token: string | null = await loginFormScenario(
                          codeOnline,
                          scenario,
                          {
                              email,
                              password,
                          },
                      );

                      setToken(token);

                      userData = await getCurrentUser(
                          getAdditionalFields({}),
                          token,
                      );
                  } catch (error) {
                      if (!(error instanceof HTTPResponseError)) {
                          throw t("errorUnknown");
                      }

                      removeToken();

                      switch (error.response.status) {
                          case 401:
                              throw t("errorCredentialsIncorrect");
                          case 429:
                              throw t("errorTooManyRequest");
                          default:
                              throw t("errorUnknown");
                      }
                  }

                  userContext.setUser(userData);

                  // Get session information
                  let sessionData: SessionType;
                  try {
                      sessionData = await getSession(codeOnline, [
                          "zoomSignature",
                      ]);
                  } catch (error) {
                      if (!(error instanceof HTTPResponseError)) {
                          throw t("errorUnknown");
                      }
                      switch (error.response.status) {
                          case 401:
                              throw t("errorYouMustBeLog");
                          case 403:
                              throw t("error403");
                          default:
                              throw t("errorUnknown");
                      }
                  }

                  setSession(sessionData);

                  navigate(`/session/${codeOnline}`, {
                      replace: true,
                  });
              };

    const [hasPasswordRequestSucceeded, setHasPasswordRequestSucceeded] =
        useState(false);
    const [email, setEmail] = useState<string | null>(null);
    const [expanded, setExpanded] = useState<string | false>(false);

    const handleChange =
        (panel: string) =>
        (event: React.SyntheticEvent, newExpanded: boolean) => {
            setExpanded(newExpanded ? panel : false);
        };

    return (
        <>
            <Accordion
                disableGutters={true}
                expanded={expanded === "panel1"}
                onChange={handleChange("panel1")}
            >
                <AccordionHeader>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                    >
                        <div>{`${t("youHaveCredentials")}`}</div>
                    </AccordionSummary>
                </AccordionHeader>

                <AccordionDetails>
                    <EmailPasswordForm
                        successMessage={
                            hasPasswordRequestSucceeded
                                ? t("passwordEmailSent")
                                : undefined
                        }
                        defaultEmailValue={email ?? undefined}
                        handleSubmit={handleSubmit}
                    />
                </AccordionDetails>
            </Accordion>

            <Accordion
                disableGutters={true}
                expanded={expanded === "panel2"}
                onChange={handleChange("panel2")}
            >
                <AccordionHeader>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                    >
                        <div>{`${t("youDontHaveCredentials")}`}</div>
                    </AccordionSummary>
                </AccordionHeader>

                <AccordionDetails>
                    <EmailForm
                        onlineCode={codeOnline}
                        onSubmitSuccess={(emailValue) => {
                            setHasPasswordRequestSucceeded(true);
                            setEmail(emailValue);
                            setExpanded("panel1");
                        }}
                        onSubmitFail={() => {
                            setHasPasswordRequestSucceeded(false);
                            setEmail(null);
                        }}
                        isPassword={true}
                    />
                </AccordionDetails>
            </Accordion>
        </>
    );
};

const AccordionHeader = styled.div`
    display: flex;
    flex-direction: row;
    background-color: #f5f5f5;
    padding: 0;
    min-height: 50px;
    align-items: center;
    border-top-left-radius: 3px;
    border-top-right-radius: 3px;
    cursor: pointer;
    border-bottom: 0;
    > div {
        text-align: left;
        font-size: 14px;
    }
    > i {
        padding: 0.75rem;
    }
`;

export default LoginPassword;
